import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { API_URL } from "../config";
import {
  isSessionExpired,
  loadSessionTimeoutConfig,
  recordActivity,
} from "../lib/sessionTimeout";

const ACTIVITY_EVENTS = ["mousedown", "keydown", "touchstart", "scroll"];
const ACTIVITY_THROTTLE_MS = 30000;
const CHECK_INTERVAL_MS = 30000;

/**
 * Enforces persisted inactivity logout while the user is authenticated.
 */
export default function SessionTimeoutManager() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const lastRecordRef = useRef(0);

  useEffect(() => {
    if (!user) return undefined;

    let cancelled = false;
    let intervalId;

    const expireSession = () => {
      logout();
      toast.info(
        "Your session expired due to inactivity. Please sign in again."
      );
      navigate("/login", { replace: true });
    };

    const checkExpired = () => {
      if (isSessionExpired()) {
        expireSession();
      }
    };

    const onActivity = () => {
      const now = Date.now();
      if (now - lastRecordRef.current < ACTIVITY_THROTTLE_MS) return;
      lastRecordRef.current = now;
      recordActivity();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        recordActivity();
        return;
      }
      checkExpired();
    };

    loadSessionTimeoutConfig(API_URL).then(() => {
      if (cancelled) return;

      checkExpired();
      intervalId = window.setInterval(checkExpired, CHECK_INTERVAL_MS);

      ACTIVITY_EVENTS.forEach((eventName) => {
        window.addEventListener(eventName, onActivity, { passive: true });
      });
      document.addEventListener("visibilitychange", onVisibilityChange);
    });

    return () => {
      cancelled = true;
      if (intervalId) window.clearInterval(intervalId);
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, onActivity);
      });
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [user, logout, navigate]);

  return null;
}
